import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { useCallback, useRef } from 'react';

/**
 * Hook that creates a delayed callback that only applies delay
 * after the first call. The first call executes immediately.
 *
 * @param callback The function to delay
 * @param delay The delay in milliseconds
 * @param options Configuration options for delay mode (throttle or debounce)
 * @returns A function that executes immediately on first call and is delayed afterward
 */
export const useDelayedCallback = <T extends (...args: any[]) => any>(
    callback: T,
    delay: number,
    options: {
        mode: 'throttle' | 'debounce';
    } = { mode: 'throttle' }
): ((...args: Parameters<T>) => void) => {
    const isFirstCallRef = useRef(true);
    const throttledFnRef = useRef<ReturnType<typeof throttle>>();
    const latestCallbackRef = useRef(callback);

    // Always keep the latest callback
    latestCallbackRef.current = callback;

    return useCallback(
        (...args: Parameters<T>) => {
            if (isFirstCallRef.current) {
                isFirstCallRef.current = false;
                latestCallbackRef.current(...args);

                const delayFn = options.mode === 'throttle' ? throttle : debounce;

                // Create delayed function that always uses latest callback
                throttledFnRef.current = delayFn(latestCallbackRef.current, delay);
            } else {
                throttledFnRef.current?.(...args);
            }
        },
        [delay, options.mode]
    );
};
